import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
        TextField,
    makeStyles,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import AssignmentIcon from "@material-ui/icons/Assignment";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";

const useStyles = makeStyles((theme) => ({
    ...getStandardButtonStyles(theme),
    dialogPaper: {
        backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${theme.palette.primary.main}40`,
        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
        color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
        overflow: "hidden",
    },
    dialogHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
        }`,
        "& h2": {
            display: "flex",
            alignItems: "center",
            fontSize: "1.15rem",
            fontWeight: 600,
            margin: 0,
            color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
        },
    },
    helpBox: {
        backgroundColor:
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
        }`,
        padding: theme.spacing(1.5, 3),
    },
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const ServiceSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required(i18n.t("serviceModal.form.name_required")),
    description: Yup.string(),
    duration: Yup.number().required(i18n.t("serviceModal.form.duration_required")),
    price: Yup.number(),
});

const ServiceModal = ({ open, onClose, serviceId }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        description: "",
        duration: 30,
        price: 0,
        reminder1Value: "24",
        reminder1Unit: "hours",
        reminder2Value: "2",
        reminder2Unit: "hours",
    };

    const [service, setService] = useState(initialState);
    const [showHelp, setShowHelp] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        const fetchService = async () => {
            if (!serviceId) return;
            try {
                const { data } = await api.get(`/services/${serviceId}`);
                if (isMounted.current) {
                    setService(data);
                }
            } catch (err) {
                toastError(err);
            }
        };

        fetchService();
    }, [serviceId, open]);

    const handleClose = () => {
        onClose();
        setService(initialState);
        setShowHelp(false);
    };

    const handleSaveService = async (values) => {
        try {
            if (serviceId) {
                await api.put(`/services/${serviceId}`, values);
                toast.success(i18n.t("serviceModal.toasts.updated"));
            } else {
                await api.post("/services", values);
                toast.success(i18n.t("serviceModal.toasts.created"));
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    className: classes.dialogPaper,
                }}
            >
                <div className={classes.dialogHeader}>
                    <h2>
                        <AssignmentIcon style={{ marginRight: 10, fontSize: 22, color: "#00b4d8" }} />
                        {serviceId
                            ? i18n.t("serviceModal.title.edit")
                            : i18n.t("serviceModal.title.add")}
                    </h2>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            size="small"
                            onClick={() => setShowHelp((prev) => !prev)}
                            title="Ayuda / Información"
                            style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="caption"
                            style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
                        >
                            BotMix | Servicios
                        </Typography>
                        <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </div>

                <Collapse in={showHelp}>
                    <Box className={classes.helpBox}>
                        <Typography
                            variant="body2"
                            style={{
                                color: "#00b4d8",
                                lineHeight: 1.5,
                            }}
                        >
                            {i18n.t("serviceModal.helpSummary") || "Define el título del servicio, tiempo de sesión en minutos, costo y recordatorios automáticos de aviso previo para la cita."}
                        </Typography>
                    </Box>
                </Collapse>
                <Formik
                    initialValues={service}
                    enableReinitialize={true}
                    validationSchema={ServiceSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveService(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("serviceModal.form.name")}
                                    autoFocus
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("serviceModal.form.description")}
                                    name="description"
                                    error={touched.description && Boolean(errors.description)}
                                    helperText={touched.description && errors.description}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("serviceModal.form.duration")}
                                    name="duration"
                                    type="number"
                                    error={touched.duration && Boolean(errors.duration)}
                                    helperText={touched.duration && errors.duration}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label={i18n.t("serviceModal.form.price")}
                                    name="price"
                                    type="number"
                                    error={touched.price && Boolean(errors.price)}
                                    helperText={touched.price && errors.price}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <div style={{ marginTop: 20 }}>
                                    <strong>Recordatorios de Cita Automáticos</strong>
                                    <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 4 }}>Estos recordatorios se aplicarán por defecto cuando se agende una cita pública a través del link del profesional.</p>
                                </div>
                                <Grid container spacing={2} style={{ marginTop: 10 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Field
                                            as={TextField}
                                            label="Recordatorio 1"
                                            name="reminder1Value"
                                            type="number"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <FormControl variant="outlined" margin="dense" fullWidth>
                                            <InputLabel>Unidad</InputLabel>
                                            <Field
                                                as={Select}
                                                name="reminder1Unit"
                                                label="Unidad"
                                            >
                                                <MenuItem value="minutes">Minutos</MenuItem>
                                                <MenuItem value="hours">Horas</MenuItem>
                                                <MenuItem value="days">Días</MenuItem>
                                            </Field>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Field
                                            as={TextField}
                                            label="Recordatorio 2"
                                            name="reminder2Value"
                                            type="number"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <FormControl variant="outlined" margin="dense" fullWidth>
                                            <InputLabel>Unidad</InputLabel>
                                            <Field
                                                as={Select}
                                                name="reminder2Unit"
                                                label="Unidad"
                                            >
                                                <MenuItem value="minutes">Minutos</MenuItem>
                                                <MenuItem value="hours">Horas</MenuItem>
                                                <MenuItem value="days">Días</MenuItem>
                                            </Field>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                            </DialogContent>
                            <DialogActions style={{ padding: "16px 24px" }}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.dangerButton}
                                >
                                    {i18n.t("common.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={classes.applyButton}
                                >
                                    {serviceId
                                        ? i18n.t("common.save")
                                        : i18n.t("common.add")}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default ServiceModal;
